import React, { useState, useCallback, memo } from "react";
import { connect } from "react-redux";
import { RootState } from "../../models/RootState";
import "./BMTable.scss";
import {
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from "@mui/material";
import {
  BookMakers,
  EventData,
  Runners,
} from "../../models/EventData";
import "../MatchOddsTable/MatchOddsTable.scss"
import MarketOddBtn from "../MarketOddBtn/marketOddBtn";
import { addExchangeBet } from "../../store/exchBetSlip/exchBetSlipAction";
import moment from "moment";
import { BsData, UserBet } from "../../models/ExchBetSlip";
import ExchBetSlip from "../ExchBetSlip/ExchBetSlip";
import { ExpandLessSharp } from "@mui/icons-material";
import { placeBet, getBetStatus } from "../../utils/bettingUtils";
import { useDispatch } from "react-redux";
import { clearExchangeBets } from "../../store/exchBetSlip/exchBetSlipReducer";
import { useCashoutLogic, RiskRow } from "../../hooks/useCashoutLogic";
import CashoutDialog from "../Cashout/CashoutDialog";
import TurboCashoutDialog from "../Cashout/TurboCashoutDialog";
import { useTranslation } from "../../hooks/useTranslation";
import { UserDetails } from "../../models/UserDetails";
import { SiteConfig } from "../../models/SiteConfig";

type StoreProps = {
  bets: BsData[];
  addExchangeBet: (data: BsData) => void;
  eventData: EventData;
  bmMarkets: BookMakers[];
  openBetsData: UserBet[];
  isOneClickBettingEnabled: boolean;
  bettingInprogress: boolean;
  turboCashoutCommission: number;
  userDetails?: UserDetails;
  siteConfig?: SiteConfig;
  dynamicTextColors?: any;
};

const BMTable: React.FC<StoreProps> = (props) => {
  const { t } = useTranslation();
  const { bmMarkets, bets, eventData, openBetsData, addExchangeBet, isOneClickBettingEnabled, bettingInprogress, turboCashoutCommission, userDetails, siteConfig, dynamicTextColors } = props;
  const dispatch = useDispatch();
  const [selectedRow, setSelectedRow] = useState<string>("");
  const [coMarket, setCoMarket] = useState<BookMakers | null>(null);
  const [confirmCashout, setConfirmCashout] = useState<boolean>(false);
  const [confirmTurboCashout, setConfirmTurboCashout] = useState<boolean>(false);
  const disabledStatus = [
    'suspended',
    'closed',
    'suspended-manually',
    'suspended manually',
    'ball_running',
    'ball running'
  ];

  // Get dynamic text color classes from Redux instead of calling function
  const accordionHeaderTextColorClass = dynamicTextColors?.accordionHeaderTextColorClass || '';
  const tableHeaderTextColorClass = dynamicTextColors?.tableHeaderTextColorClass || '';
  const tableRowTextColorClass = dynamicTextColors?.tableRowTextColorClass || '';

  // betLimitsTextColorClass uses same as accordionHeaderTextColorClass (both use --ion-background-color)
  const betLimitsTextColorClass = accordionHeaderTextColorClass;

  // Use shared cashout logic
  const {
    getTotalPL,
    getOpenBetsPLInArray,
    isHaveCashOut,
    calculateCashout,
    getCashoutProfit,
    getTurboCashoutData,
    turboCashout,
    isTurboCashoutAvailable,
    exposureMaps,
  } = useCashoutLogic(eventData?.sportId || "", bmMarkets, openBetsData, "BOOKMAKER", turboCashoutCommission);

  // Memoized callback functions to prevent unnecessary re-renders
  const memoizedGetTotalPL = useCallback(
    (marketId: string, runner: Runners) => getTotalPL(marketId, runner, bets),
    [getTotalPL, bets]
  );

  const autoCashout = async () => {
    if (coMarket == null || !eventData) return;

    const response = calculateCashout(coMarket);
    if (!response || response.oddValue == 0) return;

    // Create BsData object with all required fields like addExchangeBet
    const betData: BsData = {
      eventId: eventData.eventId,
      eventName: eventData.eventName,
      marketId: coMarket.marketId,
      marketName: coMarket.marketName,
      marketType: "BOOKMAKER",
      runnerId: response.runnerId,
      runnerName: response.runnerName,
      oddValue: +((response.oddValue * 100) - 100).toFixed(2),
      betType: response.betType as "BACK" | "LAY",
      stakeAmount: +(response.stake).toFixed(2),
      competitionId: eventData.competitionId,
      sportId: eventData.sportId,
      minLimit: coMarket?.limits?.minBetValue || 100,
      maxLimit: coMarket?.limits?.maxBetValue || 5000,
      provider: eventData?.markets?.bookMakerProvider || "",
      origin: "EXCHANGE"
    };

    // Use the same bet placement flow as addExchangeBet
    // Clear existing bets first
    dispatch(clearExchangeBets({}));

    // Place the bet directly (similar to one-click betting flow)
    const result = await placeBet(betData, dispatch, (betData: BsData) => getBetStatus(betData, dispatch));
    if (result) {
      setConfirmCashout(false);
      setCoMarket(null);
    }
  };

  const handleTurboCashout = async () => {
    if (coMarket == null || !eventData) return;
    const provider = eventData?.markets?.bookMakerProvider || "";
    try {
      await turboCashout(coMarket, eventData.sportId, eventData.eventId, provider, dispatch);
    } catch (err) {
      console.error("Turbo cashout failed:", err);
    } finally {
      // always run, success or error
      setConfirmTurboCashout(false);
      setCoMarket(null);
    }
  };

  return (
    <>
      <div className="bm-table-ctn matchodds-table-ctn">
        <div className="bm-table-content table-ctn matchodds-table-content">
          {bmMarkets && bmMarkets?.length > 0
            ? bmMarkets.map((market, index) => {
              return (
                <>
                  <Accordion
                    defaultExpanded={true}
                    className="markets-accordian">
                    <AccordionSummary
                      expandIcon={
                        <ExpandLessSharp className={`expand-icon ${accordionHeaderTextColorClass} ${accordionHeaderTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`} style={{ color: accordionHeaderTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }} />
                      }
                      aria-controls="panel1a-content"
                    >
                      <TableRow
                        className="acc-head"
                      >
                        <TableCell
                          colSpan={3}
                          className="acc-cell"
                        >
                          <div className="market-name-cell-head-ctn">
                            <div className="market-name-section">
                              <div className={`market-name ${accordionHeaderTextColorClass} ${accordionHeaderTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`} style={{ color: accordionHeaderTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }}>
                                {market.marketName}
                              </div>
                              <div className="cashout-option">
                                {isHaveCashOut(market, disabledStatus) && siteConfig?.isCashoutEnabled && eventData?.co === true && (
                                  <Button
                                    size="small"
                                    color="primary"
                                    variant="contained"
                                    className="btn cashout-btn"
                                    disabled={
                                      disabledStatus.includes(
                                        market.status?.toLowerCase() || ''
                                      ) ||
                                      getCashoutProfit(market) ==
                                      Infinity ||
                                      bettingInprogress
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCoMarket(market);
                                      setConfirmCashout(true);
                                    }}
                                  >
                                    {t("sports.cashout")}{' '}
                                    {getCashoutProfit(market) != Infinity
                                      ? `: ${getCashoutProfit(market)}`
                                      : ''}
                                  </Button>
                                )}
                                {isTurboCashoutAvailable(market, exposureMaps as Record<string, RiskRow[]>) && siteConfig?.isTurboCashoutEnabled && eventData?.co === true && (
                                  <Button
                                    size="small"
                                    color="secondary"
                                    variant="contained"
                                    className="btn cashout-btn"
                                    disabled={
                                      disabledStatus.includes(
                                        market.status.toLowerCase()
                                      ) ||
                                      bettingInprogress
                                    }
                                    onClick={() => {
                                      setCoMarket(market);
                                      setConfirmTurboCashout(true);
                                    }}
                                  >
                                    {t("sports.turboCashout")}
                                  </Button>
                                )}
                              </div>
                            </div>
                            <span className={`bet-limits-section mob-view ${betLimitsTextColorClass} ${betLimitsTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`} style={{ color: betLimitsTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }}>
                              <div>
                                {t("sports.min")}:{market?.limits?.minBetValue
                                  ? market?.limits?.minBetValue
                                  : "100"}&nbsp;
                              </div>
                              <div>
                                {t("sports.max")}:{market?.limits?.maxBetValue
                                  ? market?.limits?.maxBetValue %
                                    1000 ==
                                    0
                                    ? market?.limits?.maxBetValue /
                                    1000 +
                                    "K"
                                    : market?.limits?.maxBetValue
                                  : "5K"}
                              </div>
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TableContainer key={index} component={Paper}>
                        <Table className="bm-table">
                          <TableBody>
                            <TableRow className={`header-row ${tableHeaderTextColorClass} ${tableHeaderTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}>
                              <TableCell colSpan={1} align="left" className={`market-name-cell-head ${tableHeaderTextColorClass} ${tableHeaderTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`} style={{ color: tableHeaderTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }}>
                                <div className={`teamname-odd ${tableHeaderTextColorClass} ${tableHeaderTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`} style={{ color: tableHeaderTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }}>{t("sports.market")}</div>
                              </TableCell>
                              <TableCell colSpan={1} align="right" className={`odds-cell-head ${tableHeaderTextColorClass} ${tableHeaderTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`} style={{ color: tableHeaderTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }}>
                                <div className={`back-odd ${tableHeaderTextColorClass} ${tableHeaderTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`} style={{ color: tableHeaderTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }}>
                                  BACK
                                </div>
                              </TableCell>
                              <TableCell colSpan={1} align="left" className={`odds-cell-head ${tableHeaderTextColorClass} ${tableHeaderTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`} style={{ color: tableHeaderTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }}>
                                <div className={`lay-odd ${tableHeaderTextColorClass} ${tableHeaderTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`} style={{ color: tableHeaderTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }}>
                                  LAY
                                </div>
                              </TableCell>
                            </TableRow>
                            {market.runners?.length > 0 ? (
                              <>
                                {market.runners.map((runner) => (
                                  <BmOddsRow
                                    key={`${market.marketId}-${runner.runnerId}`}
                                    isOneClickBettingEnabled={isOneClickBettingEnabled}
                                    getOpenBetsPL={getOpenBetsPLInArray}
                                    getTotalPL={memoizedGetTotalPL}
                                    market={market}
                                    runner={runner}
                                    addExchangeBet={addExchangeBet}
                                    disabledStatus={disabledStatus}
                                    eventData={eventData}
                                    marketName={market.marketName}
                                    marketId={market.marketId}
                                    bets={bets}
                                    selectedRow={selectedRow}
                                    setSelectedRow={setSelectedRow}
                                    index={index}
                                    min={
                                      market?.limits?.minBetValue
                                        ? market?.limits?.minBetValue
                                        : 0
                                    }
                                    max={
                                      market?.limits?.maxBetValue
                                        ? market?.limits?.maxBetValue
                                        : 0
                                    }
                                    userDetails={userDetails}
                                    dynamicTextColors={dynamicTextColors}
                                  />
                                ))}
                              </>
                            ) : (
                              <>
                                <TableRow>
                                  <TableCell colSpan={3}>
                                    <div className="fm-table-msg-text">
                                      {t("sports.messages.noBookMakerForThisEvent")}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              </>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                </>
              );
            })
            : null}
        </div>
      </div>
      <CashoutDialog
        open={confirmCashout}
        onClose={() => setConfirmCashout(false)}
        onConfirm={autoCashout}
        bettingInprogress={bettingInprogress}
        cashoutAmount={coMarket ? getCashoutProfit(coMarket) : undefined}
      />
      <TurboCashoutDialog
        open={confirmTurboCashout}
        onClose={() => setConfirmTurboCashout(false)}
        onConfirm={handleTurboCashout}
        loading={bettingInprogress}
        market={coMarket}
        getTurboCashoutData={getTurboCashoutData}
        exposureMaps={exposureMaps as Record<string, RiskRow[]>}
        turboCashoutCommission={turboCashoutCommission}
      />
    </>
  );
};

type MatchOddsRowProps = {
  getOpenBetsPL: (
    marketId: string,
    runner: Runners
  ) => any;
  getTotalPL: (
    marketId: string,
    runner: Runners
  ) => any;
  marketId: string;
  runner: Runners;
  disabledStatus: string[];
  bets: BsData[];
  addExchangeBet: (data: BsData) => void;
  eventData: EventData;
  marketName: string;
  selectedRow: string;
  setSelectedRow: (data: any) => void;
  index: number;
  min: number;
  max: number;
  market: BookMakers;
  isOneClickBettingEnabled: boolean;
  userDetails?: UserDetails;
  dynamicTextColors?: any;
};

const BmOddsRow: React.FC<MatchOddsRowProps> = memo((props) => {
  const {
    marketId,
    runner,
    addExchangeBet,
    disabledStatus,
    eventData,
    marketName,
    bets,
    selectedRow,
    setSelectedRow,
    getOpenBetsPL,
    getTotalPL,
    index,
    min,
    max,
    market,
    isOneClickBettingEnabled,
    userDetails,
    dynamicTextColors
  } = props;

  // Get dynamic text color class for table rows from Redux
  const rowTextColorClass = dynamicTextColors?.tableRowTextColorClass || '';

  return (
    <>
      <TableRow className={`${rowTextColorClass} ${rowTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}>
        <TableCell className={`team-name-cell ${rowTextColorClass} ${rowTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`} style={{ color: rowTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }}>
          <div className={`team ${rowTextColorClass} ${rowTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`} style={{ color: rowTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }}>{runner.runnerName}
            {getOpenBetsPL(
              marketId,
              runner
            ).map((ret: any) =>
              ret !== null ? (
                <span className={ret >= 0 ? 'profit' : 'loss'}>
                  {ret > 0
                    ? '+' + Number(ret).toFixed(2)
                    : Number(ret).toFixed(2)}
                </span>
              ) : null
            )}
          </div>
          {getTotalPL(
            marketId,
            runner
          ).map((ret: any) =>
            ret !== null ? (
              <div className="profit-loss-box">
                <span className={ret >= 0 ? 'profit' : 'loss'}>
                  {ret > 0
                    ? '+' + Number(ret).toFixed(2)
                    : Number(ret).toFixed(2)}
                </span>
              </div>
            ) : null
          )}
        </TableCell>
        <TableCell className="odds-cell">
          <div className="odds-block">
            <MarketOddBtn
              mainValue={
                runner.backPrices && runner.backPrices[0]
                  ? runner.backPrices[0].price
                  : 0
              }
              subValue={
                runner.backPrices && runner.backPrices[0]
                  ? runner.backPrices[0].size
                  : 0
              }
              showSubValueinKformat={true}
              oddType="back-odd"
              valueType="bookmakerOdds"
              disable={
                disabledStatus.includes(eventData?.status?.toLowerCase()) ||
                disabledStatus.includes(market?.status?.toLowerCase()) ||
                disabledStatus.includes(runner?.status?.toLowerCase()) ||
                moment(eventData?.openDate).diff(
                  moment(),
                  'minutes'
                ) >
                60 * 24 * 2
              }
              onClick={() => {
                addExchangeBet({
                  eventId: eventData?.eventId,
                  eventName: eventData?.eventName,
                  marketId: marketId,
                  marketName: marketName,
                  marketType: "BOOKMAKER",
                  oddValue:
                    runner.backPrices && runner.backPrices[0]
                      ? runner.backPrices[0].price
                      : 0,
                  betType: "BACK",
                  stakeAmount: 0,
                  runnerId: runner.runnerId,
                  runnerName: runner.runnerName,
                  competitionId: eventData?.competitionId,
                  sportId: eventData?.sportId,
                  maxLimit: max,
                  minLimit: min,
                  provider: eventData?.markets?.bookMakerProvider || ""
                });
                setSelectedRow(runner.runnerId + marketName + "BOOKMAKER");
              }}
            />
          </div>
        </TableCell>
        <TableCell className="odds-cell">
          <div className="odds-block">
            <MarketOddBtn
              mainValue={
                runner.layPrices && runner.layPrices[0]
                  ? runner.layPrices[0].price
                  : 0
              }
              subValue={
                runner.layPrices && runner.layPrices[0]
                  ? runner.layPrices[0].size
                  : 0
              }
              showSubValueinKformat={true}
              oddType="lay-odd"
              valueType="bookmakerOdds"
              disable={
                disabledStatus.includes(eventData?.status?.toLowerCase()) ||
                disabledStatus.includes(market?.status?.toLowerCase()) ||
                disabledStatus.includes(runner?.status?.toLowerCase()) ||
                moment(eventData?.openDate).diff(
                  moment(),
                  'minutes'
                ) >
                60 * 24 * 2
              }
              onClick={() => {
                addExchangeBet({
                  eventId: eventData?.eventId,
                  eventName: eventData?.eventName,
                  marketId: marketId,
                  marketType: "BOOKMAKER",
                  marketName: marketName,
                  oddValue:
                    runner.layPrices && runner.layPrices[0]
                      ? runner.layPrices[0].price
                      : 0,
                  betType: "LAY",
                  stakeAmount: 0,
                  runnerId: runner.runnerId,
                  runnerName: runner.runnerName,
                  competitionId: eventData?.competitionId,
                  sportId: eventData?.sportId,
                  maxLimit: max,
                  minLimit: min,
                  provider: eventData?.markets?.bookMakerProvider || ""
                });

                setSelectedRow(runner.runnerId + marketName + "BOOKMAKER");
              }
              }
            />
          </div>
        </TableCell>
      </TableRow>
      {!isOneClickBettingEnabled && bets?.length > 0 &&
        bets?.[0]?.marketName === marketName &&
        bets?.[0]?.marketId === marketId &&
        bets?.[0]?.runnerId === runner.runnerId &&
        <TableRow className="inline-betslip">
          <TableCell colSpan={3}>
            {' '}
            <ExchBetSlip balance={0} exposure={0} />{' '}
          </TableCell>
        </TableRow>
      }
    </>
  );
});

const mapStateToProps = (state: RootState) => {
  return {
    bets: state.exchBetSlip.bets,
    eventData: state.exchangeSports.eventInfo,
    openBetsData: state.exchBetSlip.openBets,
    isOneClickBettingEnabled: state.exchBetSlip.isOneClickBettingEnabled,
    bettingInprogress: state.exchBetSlip.bettingInprogress,
    turboCashoutCommission: state.exchangeSports.turboCashoutCommission,
    userDetails: state.userDetails?.userDetails,
    siteConfig: state.userDetails?.siteConfig,
    dynamicTextColors: state.userDetails?.dynamicTextColors,
  };
};

const mapDispatchToProps = (dispatch: Function) => {
  return {
    addExchangeBet: (data: BsData) => dispatch(addExchangeBet(data)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(BMTable);